import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getTodayOutlet, formatDate, getWeekTasks } from '../utils/constants';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Save, FileText, Clock, CheckCircle2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Workspace() {
  const [activeTab, setActiveTab] = useState('tasks');
  const [content, setContent] = useState({
    brief: '',
    analysis: '',
    lede: '',
    headline: '',
    attribution: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  // Tasks state
  const [taskSettings, setTaskSettings] = useState(null);
  const [completedTasks, setCompletedTasks] = useState([]);

  const todayOutlet = getTodayOutlet();
  const today = formatDate();

  useEffect(() => {
    loadTodayContent();
    fetchTaskData();
  }, []);

  const fetchTaskData = async () => {
    try {
      const [settingsRes, entriesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/settings`),
        axios.get(`${BACKEND_URL}/api/daily/entries?date=${today}`)
      ]);
      setTaskSettings(settingsRes.data);
      if (entriesRes.data.length > 0) {
        setCompletedTasks(entriesRes.data[0].tasks_completed || []);
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  };

  const toggleTask = async (taskId) => {
    const newCompleted = completedTasks.includes(taskId)
      ? completedTasks.filter(id => id !== taskId)
      : [...completedTasks, taskId];
    setCompletedTasks(newCompleted);
    try {
      await axios.post(`${BACKEND_URL}/api/daily/entries`, {
        date: today,
        outlet: todayOutlet?.name || 'General',
        tasks_completed: newCompleted
      });
      toast.success('Progress saved!');
    } catch (error) {
      toast.error('Failed to save progress');
    }
  };

  const loadTodayContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/writing/pieces?date=${today}`);
      const pieces = response.data;
      
      const newContent = { ...content };
      pieces.forEach(piece => {
        if (piece.type === 'brief') newContent.brief = piece.content;
        if (piece.type === 'analysis') newContent.analysis = piece.content;
        if (piece.type === 'lede') newContent.lede = piece.content;
        if (piece.type === 'headline') newContent.headline = piece.content;
        if (piece.type === 'attribution') newContent.attribution = piece.content;
      });
      setContent(newContent);
    } catch (error) {
      console.error('Failed to load content:', error);
    }
  };

  const wordCount = (text) => {
    return text.trim().split(/\s+/).filter(w => w).length;
  };

  const handleSave = async (type) => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/writing/pieces`, {
        date: today,
        type,
        content: content[type],
        word_count: wordCount(content[type]),
        outlet: todayOutlet?.name || 'General'
      });
      toast.success('Saved successfully!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAsClip = async (type) => {
    if (!content[type].trim()) {
      toast.error('Nothing to save');
      return;
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/clips`, {
        title: `${type.charAt(0).toUpperCase() + type.slice(1)} - ${today}`,
        content: content[type],
        outlet: todayOutlet?.name || 'General',
        date: today,
        type
      });
      toast.success('Saved to portfolio!');
    } catch (error) {
      toast.error('Failed to save to portfolio');
    }
  };

  const TextEditor = ({ type, label, placeholder, targetWords }) => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-lg font-bold tracking-tight">{label}</Label>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground font-['IBM_Plex_Sans']">
            {wordCount(content[type])} / {targetWords} words
          </span>
          <Button
            data-testid={`btn-save-${type}`}
            onClick={() => handleSave(type)}
            disabled={saving}
            size="sm"
            variant="outline"
            className="uppercase tracking-wide text-xs"
          >
            <Save className="w-3 h-3 mr-2" />
            Save
          </Button>
          <Button
            data-testid={`btn-clip-${type}`}
            onClick={() => handleSaveAsClip(type)}
            size="sm"
            className="uppercase tracking-wide text-xs"
          >
            <FileText className="w-3 h-3 mr-2" />
            Save as Clip
          </Button>
        </div>
      </div>
      
      <textarea
        data-testid={`editor-${type}`}
        value={content[type]}
        onChange={(e) => setContent({ ...content, [type]: e.target.value })}
        placeholder={placeholder}
        className="w-full min-h-[400px] p-6 bg-card border border-border rounded-sm editor-workspace focus:ring-1 focus:ring-primary resize-none"
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-8 py-6">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2 font-['IBM_Plex_Sans']">
            Writing Workspace
          </p>
          <h1 className="text-3xl font-bold tracking-tight mb-2">
            {todayOutlet ? todayOutlet.name : 'General Practice'}
          </h1>
          <p className="text-muted-foreground font-['IBM_Plex_Sans']">
            {todayOutlet ? todayOutlet.focus : 'Practice your craft'}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-8 bg-muted">
            <TabsTrigger
              value="tasks"
              data-testid="tab-tasks"
              className="uppercase tracking-wide text-xs font-medium"
            >
              Today's Tasks
            </TabsTrigger>
            <TabsTrigger
              value="brief"
              data-testid="tab-brief"
              className="uppercase tracking-wide text-xs font-medium"
            >
              Hard-News Brief
            </TabsTrigger>
            <TabsTrigger 
              value="analysis" 
              data-testid="tab-analysis"
              className="uppercase tracking-wide text-xs font-medium"
            >
              Analysis/Nut Graf
            </TabsTrigger>
            <TabsTrigger 
              value="lede" 
              data-testid="tab-lede"
              className="uppercase tracking-wide text-xs font-medium"
            >
              Lede Rewrites
            </TabsTrigger>
            <TabsTrigger 
              value="headline" 
              data-testid="tab-headline"
              className="uppercase tracking-wide text-xs font-medium"
            >
              Headlines
            </TabsTrigger>
            <TabsTrigger 
              value="attribution" 
              data-testid="tab-attribution"
              className="uppercase tracking-wide text-xs font-medium"
            >
              Attribution
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tasks">
            {!taskSettings ? (
              <div className="flex items-center justify-center h-48 text-muted-foreground">Loading...</div>
            ) : (() => {
              const tasks = getWeekTasks(taskSettings.current_week);
              const completionRate = tasks.length ? (completedTasks.length / tasks.length) * 100 : 0;
              return (
                <div>
                  <div className="mb-6 p-6 bg-card border border-border rounded-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary" />
                        <span className="font-bold font-['IBM_Plex_Sans'] text-sm">
                          {completedTasks.length} / {tasks.length} completed
                        </span>
                      </div>
                      <span className="text-sm text-muted-foreground font-['IBM_Plex_Sans']">
                        {Math.round(completionRate)}%
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-1.5">
                      <div
                        className="bg-primary h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${completionRate}%` }}
                      />
                    </div>
                  </div>
                  <div className="space-y-3">
                    {tasks.map((task, index) => (
                      <div
                        key={task.id}
                        data-testid={`task-${task.id}`}
                        className={`p-5 bg-card border rounded-sm transition-all duration-200 ${
                          completedTasks.includes(task.id)
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          <Checkbox
                            data-testid={`checkbox-${task.id}`}
                            checked={completedTasks.includes(task.id)}
                            onCheckedChange={() => toggleTask(task.id)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-1">
                              <h3 className={`font-bold tracking-tight ${
                                completedTasks.includes(task.id) ? 'text-muted-foreground line-through' : ''
                              }`}>
                                {index + 1}. {task.title}
                              </h3>
                              <div className="flex items-center gap-1.5 text-sm text-muted-foreground font-['IBM_Plex_Sans']">
                                <Clock className="w-3.5 h-3.5" />
                                {task.time}
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground font-['IBM_Plex_Sans']">
                              {task.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="brief">
            <TextEditor
              type="brief"
              label="Hard-News Brief (20-25 min)"
              placeholder="Write a one-paragraph lede (≤35 words) with the 5Ws+H and strongest verb. Then add 1-2 grafs in descending importance. Check AP style for numbers, dates, titles..."
              targetWords="150-250"
            />
          </TabsContent>

          <TabsContent value="analysis">
            <TextEditor
              type="analysis"
              label="Analysis / Feature Nut Graf (15-20 min)"
              placeholder="Draft a nut graf that frames the tension, stakes, and context, then add one vivid detail or expert quote. Apply Economist-style clarity: cut jargon, replace buzzwords with plain words..."
              targetWords="150-250"
            />
          </TabsContent>

          <TabsContent value="lede">
            <TextEditor
              type="lede"
              label="Lede Rewrite x3 (6 min)"
              placeholder="Rewrite the day's news lede as: (a) summary, (b) delayed-ID, (c) scene-setter. Keep the strongest one..."
              targetWords="100-150"
            />
          </TabsContent>

          <TabsContent value="headline">
            <TextEditor
              type="headline"
              label="Headline Pack (4 min)"
              placeholder="Write 3 hed options: a straight hed, a kicker+hed pair, and an SEO hed. Follow language economy principles..."
              targetWords="50-100"
            />
          </TabsContent>

          <TabsContent value="attribution">
            <TextEditor
              type="attribution"
              label="Attribution Check (3-5 min)"
              placeholder="Line-edit to attribute every non-original fact; prefer named docs/people; specify what you haven't verified. Use explicit, specific sources..."
              targetWords="100-200"
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

